import React from 'react';
import { TFunction } from 'i18next';
import {
  FieldChangeCallBackArgs,
  FieldItem,
  Values,
} from '@lib/interfaces/form';
import _get from 'lodash/get';
import FieldTitleRender from '@lib/components/FieldTitleRender/FieldTitleRender';
import { MAX_TEXT_EDITOR_FIELD_LENGTH, Steps } from '@lib/enums/form';
import Input from '@lib/components/Input/Input';
import Select, { SelectProps } from '@lib/components/Select/Select';
import {
  getFieldValidation,
  getOptionValidation,
} from '@lib/utils/validationUtils';
import { getTaskPriorityOptions } from '@lib/utils/priority';
import styles from '@lib/assets/styles/Form.module.scss';
import { GetLoadOptions } from '@lib/components/Select/types';
import MultiSelectValueContainer from '@lib/components/Select/components/MultiSelectValueContainer';
import { FieldNames } from 'routes/Task/enums';
import VersionOption from 'components/VersionOption/VersionOption';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import TextEditor from '@lib/components/TextEditor/TextEditor';
import ChipOption from '@lib/components/Select/components/ChipOption';
import ChipValueContainer from '@lib/components/Select/components/ChipValueContainer';
import getCreateTaskScopeFields from './getCreateTaskScopeFields';
import getEditTaskScopeFields from './getEditTaskScopeFields';
import getDisabledTaskScopeFields from './getDisabledTaskScopeFields';

const multiSelectComponents = {
  ValueContainer: MultiSelectValueContainer,
};

type TaskFieldsArgs = {
  canQrCodeRelatedInfoUpdate?: boolean;
  getApproversLoadOptions: GetLoadOptions;
  getAssetsLoadOptions: GetLoadOptions;
  getSiteAreasLoadOptions: GetLoadOptions;
  getSitesLoadOptions: GetLoadOptions;
  getTemplateVersionsLoadOptions?: GetLoadOptions;
  getTemplatesLoadOptions?: GetLoadOptions;
  getDocumentationsLoadOptions?: GetLoadOptions;
  getDepartmentsLoadOptions?: GetLoadOptions;
  openAreasModalTableMultiselect?: () => void;
  openAssetsModalTableMultiselect?: () => void;
  openAreasModalTableMultiunselect?: () => void;
  openAssetsModalTableMultiunselect?: () => void;
  openDocumentsModalTableMultiselect?: () => void;
  openDepartmentsModalTableMultiselect?: () => void;
  isCreateForm?: boolean;
  isDocumentationLocked?: boolean;
  onCreateNewFormTemplate?: () => void;
  onViewFormTemplate?: (
    templateFormId: string,
    templateFormVersionId: string,
  ) => void;
  step?: Steps;
  t: TFunction<'translation', undefined>;
  isCreate: boolean;
};

export default function getTaskFields(args: TaskFieldsArgs): FieldItem[] {
  const {
    canQrCodeRelatedInfoUpdate = true,
    getDocumentationsLoadOptions,
    getDepartmentsLoadOptions,
    getTemplateVersionsLoadOptions,
    getTemplatesLoadOptions,
    isDocumentationLocked = false,
    openDocumentsModalTableMultiselect,
    openDepartmentsModalTableMultiselect,
    step = Steps.step1,
    onCreateNewFormTemplate,
    onViewFormTemplate,
    t,
    isCreate,
  } = args;
  return [
    {
      name: FieldNames.taskDetails,
      customRender: () => (
        <FieldTitleRender key="task-details" title={t('task-details')} />
      ),
      step,
    },
    {
      name: FieldNames.taskName,
      label: t('task-name'),
      element: Input,
      validation: getFieldValidation({ t, required: true }),
      step,
    },
    {
      name: FieldNames.taskDescription,
      label: t('task-description'),
      element: TextEditor,
      validation: getFieldValidation({
        maxLength: MAX_TEXT_EDITOR_FIELD_LENGTH,
        t,
      }),
      step,
    },
    {
      name: FieldNames.priority,
      label: t('task-priority'),
      element: Select,
      validation: getOptionValidation({ t, required: true }),
      componentProps: {
        options: getTaskPriorityOptions(),
        isSearchable: false,
        components: {
          ValueContainer: ChipValueContainer,
          Option: ChipOption,
        },
      },
      step,
    },
    {
      name: FieldNames.templateTitle,
      customRender: () => (
        <FieldTitleRender
          key="attach-form-and-documents"
          title={t('attach-form-and-documents')}
          isBlockStart
        />
      ),
      step,
    },
    {
      name: FieldNames.template,
      label: t('form-template'),
      element: Select,
      validation: getOptionValidation({ t, required: true }),
      componentProps: {
        disabled: !canQrCodeRelatedInfoUpdate,
        disabledTooltipProps: canQrCodeRelatedInfoUpdate
          ? undefined
          : {
              body: t('disabled-field-desc-for-task-form-template'),
            },
        isAsync: true,
        isClearable: false,
        getLoadOptions: getTemplatesLoadOptions,
        defaultOptions: false,
        createNewOptionProps: {
          title: t('create-new'),
          onCLick: onCreateNewFormTemplate,
        },
      } as SelectProps,
      fieldChangeCallBack: ({
        type,
        setValue,
        name,
      }: FieldChangeCallBackArgs) => {
        if (type === 'change' && name === FieldNames.template) {
          setValue(FieldNames.templateVersion, null, { shouldValidate: false });
        }
      },
      step,
      formItemClassName: styles.formColumn9,
      useKeyRefreshOnWindowFocus: true,
      isVisibleFn: () =>
        !!getTemplatesLoadOptions && !!getTemplateVersionsLoadOptions,
    },
    {
      name: FieldNames.templateVersion,
      label: t('version'),
      element: Select,
      validation: getOptionValidation({ t, required: true }),
      componentProps: {
        isAsync: true,
        isClearable: false,
        getLoadOptions: getTemplateVersionsLoadOptions,
        defaultOptions: false,
        components: {
          Option: VersionOption,
        },
        disabledTooltipProps: {
          body: t('choose-form-template-first'),
        },
      } as SelectProps,
      isDisabledFn: (values: Values, name?: string) => {
        if (!name) return false;
        const depsName = name.replace(
          FieldNames.templateVersion,
          `${FieldNames.template}.value`,
        );
        const depsValue = _get(values, depsName);
        return depsValue === undefined;
      },
      step,
      formItemClassName: styles.formColumn3,
      getFieldKeyFn: (values: Values, name?: string) => {
        const templateId = _get(values, `${FieldNames.template}.value`);
        if (templateId) {
          return `${name}-${FieldNames.templateVersion}-${templateId}`;
        }
        return FieldNames.templateVersion;
      },
      useKeyRefreshOnWindowFocus: true,
      isVisibleFn: () =>
        !!getTemplatesLoadOptions && !!getTemplateVersionsLoadOptions,
    },
    {
      name: FieldNames.templateVersionViewBtn,
      customRender: (values: Values) =>
        onViewFormTemplate ? (
          <Button
            buttonText={t('view-template')}
            buttonType={ButtonTypes.primaryOutlined}
            buttonSize={ButtonSizes.small}
            rightIcon="arrow_outward"
            onClick={() =>
              onViewFormTemplate(
                _get(values, [FieldNames.template, 'value']),
                _get(values, [FieldNames.templateVersion, 'value']),
              )
            }
          />
        ) : (
          <div />
        ),
      isVisibleFn: (values: Values) =>
        onViewFormTemplate &&
        _get(values, [FieldNames.template, 'value']) &&
        _get(values, [FieldNames.templateVersion, 'value']) &&
        !!getTemplatesLoadOptions &&
        !!getTemplateVersionsLoadOptions,
      step,
    },
    ...(isDocumentationLocked
      ? []
      : [
          {
            name: FieldNames.documentationLinks,
            label: t('documents'),
            element: Select,
            componentProps: {
              isAsync: true,
              isMulti: true,
              getLoadOptions: getDocumentationsLoadOptions,
              defaultOptions: false,
              isClearable: true,
              isSearchable: false,
              openMenuOnClick: false,
              openMenuOnFocus: false,
              menuIsOpen: false,
              onSelectWrapperMouseDown: openDocumentsModalTableMultiselect,
              components: multiSelectComponents,
            },
            step,
            isOptionalFn: () => true,
          },
        ]),
    {
      name: FieldNames.departmentsAndVisibilityTitle,
      customRender: () => (
        <FieldTitleRender
          key="departments-and-visibility"
          title={t('departments-and-visibility')}
          tooltipText={t('departments-and-visibility-desc')}
          isBlockStart
        />
      ),
      step,
    },
    {
      name: FieldNames.departmentLinks,
      label: t('task-departments-label'),
      element: Select,
      description: t('task-departments-leave-empty-desc'),
      componentProps: {
        isAsync: true,
        isMulti: true,
        getLoadOptions: getDepartmentsLoadOptions,
        defaultOptions: false,
        isClearable: true,
        isSearchable: false,
        openMenuOnClick: false,
        openMenuOnFocus: false,
        menuIsOpen: false,
        onSelectWrapperMouseDown: openDepartmentsModalTableMultiselect,
        components: multiSelectComponents,
      },
      step,
      isOptionalFn: () => true,
    },
    ...(isCreate ? getCreateTaskScopeFields(args) : []),
    ...(!isCreate && canQrCodeRelatedInfoUpdate
      ? getEditTaskScopeFields(args)
      : []),
    ...(!isCreate && !canQrCodeRelatedInfoUpdate
      ? getDisabledTaskScopeFields(args)
      : []),
  ];
}
